/**
 * Value formatting shared across the app. Every screen was writing its own
 * `usd` / `money` / `fee` one-liner, and they had quietly drifted: some crashed
 * on null, some printed "$NaN", some showed three decimals.
 */

/** What a figure reads as when there isn't one. */
export const NA = 'N/A';

/**
 * `decimals` unset means "up to two, none if they're zero" — $10,000 and
 * $9,952.01. Passing a number fixes the places, which is what an invoice line
 * wants: $50.00, not $50.
 */
const digits = (decimals) =>
  decimals == null
    ? { maximumFractionDigits: 2 }
    : { minimumFractionDigits: decimals, maximumFractionDigits: decimals };

/** Anything that isn't a usable number — including the API's empty strings. */
const isBlank = (value) => value == null || value === '' || !Number.isFinite(Number(value));

/**
 * A non-negative amount rounded half-up to `places` the way it reads on paper
 * (N92). Floating point stores 0.205 as 0.20499999…, which `toLocaleString`
 * then rounds down to 0.20 — so the noise is cleared first (15 significant
 * digits), and the rounding done by shifting the decimal point in the string,
 * which is exact. Anything the shift can't express is passed through as is.
 */
const roundHalfUp = (amount, places) => {
  const clean = Number(amount.toPrecision(15));
  const rounded = Number(`${Math.round(Number(`${clean}e${places}`))}e-${places}`);
  return Number.isFinite(rounded) ? rounded : amount;
};

/**
 * Money the way the app writes it: a leading $, thousands separators, at most
 * two decimals.
 *
 * Takes the value as it comes off the API — figures arrive as strings ("10000")
 * about as often as numbers — and answers `fallback` for anything that isn't a
 * number, so a missing figure reads as missing rather than "$NaN".
 */
export const usd = (value, { fallback = NA, decimals } = {}) => {
  if (isBlank(value)) return fallback;
  const n = Number(value);
  // The minus goes before the symbol — "-$37.07", not "$-37.07", which is what
  // `toLocaleString` on its own produces.
  const amount = roundHalfUp(Math.abs(n), decimals ?? 2).toLocaleString('en-US', digits(decimals));
  return `${n < 0 ? '-' : ''}$${amount}`;
};

/**
 * The same, signed — how a P/L figure is written: +$1,240.50, -$980.00. The
 * sign sits outside the amount, so it reads before the eye reaches the digits.
 */
export const signedUsd = (value, { fallback = NA, decimals } = {}) => {
  if (isBlank(value)) return fallback;
  const n = Number(value);
  const rounded = roundHalfUp(Math.abs(n), decimals ?? 2);
  const amount = rounded.toLocaleString('en-US', digits(decimals));
  // Nothing has no direction: a value that rounds to zero is "$0.00", not
  // "+$0.00" or "-$0.00" (N89) — which also covers a -0.004 that would
  // otherwise print a minus in front of zero.
  if (rounded === 0) return `$${amount}`;
  return `${n < 0 ? '-' : '+'}$${amount}`;
};

const AGO_UNITS = [
  ['year', 31536000],
  ['month', 2592000],
  ['week', 604800],
  ['day', 86400],
  ['hour', 3600],
  ['minute', 60],
];

/**
 * One formatter per language, made the first time it is asked for. The
 * language is the one I18nProvider writes on <html> — read from there rather
 * than imported, so this file stays free of the app's i18n setup.
 */
const relativeFormats = {};
const relative = () => {
  const lang = (typeof document !== 'undefined' && document.documentElement.lang) || 'en';
  if (!relativeFormats[lang]) relativeFormats[lang] = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
  return relativeFormats[lang];
};

/**
 * An ISO timestamp as "2 minutes ago" / "yesterday" — the largest unit that
 * still has a whole number in it, which is how a notification row reads a date
 * nobody wants the exact hour of.
 */
export const timeAgo = (value, { fallback = NA } = {}) => {
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return fallback;
  const seconds = Math.trunc((ms - Date.now()) / 1000);
  const [unit, size] = AGO_UNITS.find(([, s]) => Math.abs(seconds) >= s) ?? ['second', 1];
  // Truncated, not rounded: 90 seconds is "1 minute ago" until the second one
  // has actually passed, the way every other feed writes it.
  return relative().format(Math.trunc(seconds / size), unit);
};

/**
 * An account stage as the app writes it. The API pads the phase number —
 * "Phase 01", "Phase 02" — which reads like a code next to every other plain
 * figure on the page, so the number is unpadded: "Phase 1", "Phase 2". Any
 * other stage ("Qualified", "Funded") passes through untouched.
 */
export const phaseLabel = (stage) =>
  typeof stage === 'string'
    ? stage.replace(/\bphase[\s_-]*0*(\d+)\b/gi, (_, n) => `Phase ${n}`)
    : stage;
